import { styled } from '@mui/material/styles';
import { getIdvThemeTokens } from '../themeTokens';
import type { IdvPolicyStepState } from '../types';
import type { PolicyBadgeTone, PolicyStepSourceKind } from './types';

const markerFor = (theme: Parameters<typeof getIdvThemeTokens>[0], state: IdvPolicyStepState): string => {
  const t = getIdvThemeTokens(theme);
  if (state === 'done') return t.doneMarker;
  if (state === 'next') return t.nextStep;
  if (state === 'skipped') return t.skippedMarker;
  return t.violet;
};

export const PolicyRoot = styled('section')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    margin: '16px 0 0',
    padding: 16,
    border: `1px solid ${t.border}`,
    borderRadius: 10,
    background: t.surfaceSoft,
  };
});

export const PolicyHead = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 16,
  marginBottom: 14,
  [theme.breakpoints.down(760)]: {
    alignItems: 'stretch',
    flexDirection: 'column',
  },
}));

export const PolicyEyebrow = styled('p')(({ theme }) => ({
  margin: '0 0 4px',
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}));

export const PolicyTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.3,
  color: getIdvThemeTokens(theme).text,
}));

export const PolicyCopy = styled('p')(({ theme }) => ({
  margin: '4px 0 0',
  color: getIdvThemeTokens(theme).textMeta,
  fontSize: 12,
  lineHeight: 1.45,
}));

export const PolicyAside = styled('div')(({ theme }) => ({
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 8,
  [theme.breakpoints.down(760)]: {
    alignItems: 'stretch',
  },
}));

export const PolicyAdminLink = styled('a')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    minHeight: 34,
    padding: '7px 12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${t.borderStrong}`,
    borderRadius: 7,
    color: t.markSecondaryText,
    background: t.surface,
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      borderColor: t.chipHoverBorder,
      color: t.violetDark,
    },
  };
});

export const PolicyBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: PolicyBadgeTone }>(({ theme, tone }) => {
  const t = getIdvThemeTokens(theme);
  const variants: Record<PolicyBadgeTone, { color: string; background: string }> = {
    warning: { color: t.amber, background: t.nextFill },
    danger: { color: t.red, background: t.banner.danger.background },
    info: { color: t.banner.info.color, background: t.tagOverrideBg },
    neutral: { color: t.textMeta, background: t.tagDefaultBg },
  };
  return {
    flex: '0 0 auto',
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    ...variants[tone],
  };
});

export const Timeline = styled('ol')(({ theme }) => ({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 10,
  [theme.breakpoints.down(980)]: {
    gridTemplateColumns: '1fr 1fr',
  },
  [theme.breakpoints.down(760)]: {
    gridTemplateColumns: '1fr',
  },
}));

export const TimelineStep = styled('li', {
  shouldForwardProp: (prop) => prop !== 'stepState',
})<{ stepState: IdvPolicyStepState }>(({ theme, stepState }) => {
  const t = getIdvThemeTokens(theme);
  const isNext = stepState === 'next';
  return {
    position: 'relative',
    padding: '12px 12px 12px 14px',
    border: `1px solid ${isNext ? t.nextBorder : t.border}`,
    borderRadius: 8,
    background: isNext ? t.nextFill : t.surface,
    '&::before': {
      position: 'absolute',
      top: 14,
      left: 0,
      width: 3,
      height: 18,
      content: '""',
      borderRadius: 999,
      background: markerFor(theme, stepState),
    },
  };
});

export const StepLabel = styled('span')(({ theme }) => ({
  display: 'block',
  color: getIdvThemeTokens(theme).stepLabel,
  fontSize: 12,
  fontWeight: 700,
}));

export const StepMeta = styled('span')(({ theme }) => ({
  display: 'block',
  marginTop: 4,
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 11,
}));

export const StepTag = styled('span', {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: PolicyStepSourceKind }>(({ theme, kind }) => {
  const t = getIdvThemeTokens(theme);
  const override = kind === 'override';
  return {
    display: 'inline-flex',
    marginTop: 7,
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    color: override ? t.tagOverrideColor : t.textMeta,
    background: override ? t.tagOverrideBg : t.tagDefaultBg,
  };
});
